import React from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  Euro,
  ExternalLink,
  Gift,
  LoaderCircle,
  MapPin,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import "./HomebuyerOssPartnersPage.css";
import HomebuyerOssReviewModal from "./HomebuyerOssReviewSubmitPage";
import { EUROPEAN_COUNTRIES } from "../../../data/countries";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

function getServiceLabel(service, language) {
  return (
    service?.[`service_label_${language}`] ||
    service?.[`label_${language}`] ||
    service?.service_label ||
    service?.label ||
    service?.service_code ||
    "—"
  );
}

function formatCost(value) {
  if (!value) return "";
  return String(value).includes("€") ? value : `${value} €`;
}

function hasSubsidyExtraInfo(service = {}) {
  return Boolean(
    service.subsidy_status === "YES" &&
    (service.subsidy_information_url ||
      service.subsidy_information_label ||
      service.subsidy_notes),
  );
}

function formatDisplayLabel(value = "") {
  if (!value) return "";

  return String(value)
    .replace(/_/g, " ")
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function normalizeCountryCode(value = "") {
  const normalized = String(value || "")
    .trim()
    .toUpperCase();

  if (!normalized) {
    return "";
  }

  const country = EUROPEAN_COUNTRIES.find(
    (item) =>
      String(item.code || "").toUpperCase() === normalized ||
      String(item.label || "").toUpperCase() === normalized,
  );

  return country?.code || normalized;
}

function formatCountryLabel(value = "") {
  const normalized = String(value || "")
    .trim()
    .toUpperCase();

  const country = EUROPEAN_COUNTRIES.find(
    (item) =>
      String(item.code || "").toUpperCase() === normalized ||
      String(item.label || "").toUpperCase() === normalized,
  );

  return country?.label || formatDisplayLabel(value);
}

function formatRegionCountry(region, country) {
  return [formatDisplayLabel(region), formatCountryLabel(country)]
    .filter(Boolean)
    .join(" · ");
}

function formatBudgetInput(value = "") {
  const digits = String(value || "").replace(/[^\d]/g, "");
  if (!digits) return "";

  return Number(digits).toLocaleString("en-US");
}

function getBudgetNumericValue(value = "") {
  return String(value || "").replace(/[^\d]/g, "");
}

export default function HomebuyerOssPartnersPage() {
  const { t, i18n } = useTranslation([
    "homebuyer-oss-partners",
    "homebuyer-oss-review-submit",
  ]);
  const language = i18n.language?.split("-")?.[0] || "en";

  const navigate = useNavigate();
  const location = useLocation();
  const { getAccessTokenSilently } = useAuth0();

  const isPrePurchaseUrl = location.pathname.includes("/pre-purchase");

  const {
    dossierId = null,

    propertyDetails = {},

    selectedMeasures = [],

    notes = "",

    epcFileName = "",

    epcFile = null,

    requestType: stateRequestType,

    prePurchaseDetails: initialPrePurchaseDetails = {},

    homebuyerCountry: stateHomebuyerCountry = "",

    marketRegionId: stateMarketRegionId = null,

    marketRegion: stateMarketRegion = null,
    changeOssMode = false,
    previousOssRequestId = null,
    previousOssName = "",
    previousOssOrganisationId = null,
  } = location.state || {};

  const requestType =
    stateRequestType || (isPrePurchaseUrl ? "PRE_PURCHASE" : "RENOVATION");

  const isPrePurchase = requestType === "PRE_PURCHASE";
  const outletContext = useOutletContext() || {};
  const profile = outletContext.profile || {};

  const resolvedHomebuyerCountry =
    stateHomebuyerCountry ||
    propertyDetails?.country ||
    profile?.country_of_property_purchase ||
    profile?.country_of_residence ||
    "";

  const resolvedHomebuyerCountryCode = normalizeCountryCode(
    resolvedHomebuyerCountry,
  );

  const initialMarketRegionId =
    stateMarketRegionId ||
    propertyDetails?.market_region_id ||
    propertyDetails?.market_region?.id ||
    null;

  const initialMarketRegion =
    stateMarketRegion || propertyDetails?.market_region || null;

  const [marketRegions, setMarketRegions] = React.useState(
    initialMarketRegion ? [initialMarketRegion] : [],
  );

  const [selectedMarketRegionId, setSelectedMarketRegionId] = React.useState(
    initialMarketRegionId,
  );

  const [loadingMarketRegions, setLoadingMarketRegions] = React.useState(false);

  const resolvedMarketRegionId =
    selectedMarketRegionId || initialMarketRegionId || null;

  const resolvedMarketRegion = React.useMemo(() => {
    if (!resolvedMarketRegionId) {
      return null;
    }

    const loadedRegion = marketRegions.find(
      (item) => String(item.id) === String(resolvedMarketRegionId),
    );

    if (loadedRegion) {
      return loadedRegion;
    }

    if (
      initialMarketRegion &&
      String(initialMarketRegion.id) === String(resolvedMarketRegionId)
    ) {
      return initialMarketRegion;
    }

    return null;
  }, [marketRegions, resolvedMarketRegionId, initialMarketRegion]);

  const [loading, setLoading] = React.useState(true);

  const [error, setError] = React.useState("");

  const [ossList, setOssList] = React.useState([]);

  const [selectedOssId, setSelectedOssId] = React.useState(null);

  const [filterType, setFilterType] = React.useState("ALL");

  const [showReviewModal, setShowReviewModal] = React.useState(false);

  const [prePurchaseDetails, setPrePurchaseDetails] = React.useState({
    preferred_location:
      initialPrePurchaseDetails.preferred_location ||
      initialMarketRegion?.name ||
      "",

    property_type: initialPrePurchaseDetails.property_type || "",

    timeline: initialPrePurchaseDetails.timeline || "",

    budget_range: initialPrePurchaseDetails.budget_range || "",

    notes: initialPrePurchaseDetails.notes || "",
  });

  function handlePrePurchaseChange(e) {
    const { name, value } = e.target;

    setPrePurchaseDetails((prev) => ({
      ...prev,
      [name]: name === "budget_range" ? formatBudgetInput(value) : value,
    }));
  }

  React.useEffect(() => {
    if (!isPrePurchase) {
      return undefined;
    }

    if (!resolvedHomebuyerCountryCode) {
      setMarketRegions([]);
      setSelectedMarketRegionId(null);

      return undefined;
    }

    let active = true;

    async function loadMarketRegions() {
      try {
        setLoadingMarketRegions(true);

        const params = new URLSearchParams({
          country_code: resolvedHomebuyerCountryCode,

          require_lender: "true",
        });

        const response = await fetch(
          `${API_BASE}/api/market-regions/?${params.toString()}`,
        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            data?.detail ||
              t(
                "errors.marketRegionsLoadFailed",
                "Could not load market regions.",
              ),
          );
        }

        if (!active) {
          return;
        }

        const results = Array.isArray(data?.results) ? data.results : [];

        setMarketRegions(results);

        setSelectedMarketRegionId((current) => {
          if (
            current &&
            results.some((region) => String(region.id) === String(current))
          ) {
            return current;
          }

          /*
           * Automatically select the market
           * when there is only one.
           */
          if (results.length === 1) {
            return results[0].id;
          }

          return null;
        });
      } catch (loadError) {
        if (!active) {
          return;
        }

        console.error("Could not load market regions:", loadError);

        setMarketRegions([]);

        setError(
          loadError?.message ||
            t(
              "errors.marketRegionsLoadFailed",
              "Could not load market regions.",
            ),
        );
      } finally {
        if (active) {
          setLoadingMarketRegions(false);
        }
      }
    }

    loadMarketRegions();

    return () => {
      active = false;
    };
  }, [isPrePurchase, resolvedHomebuyerCountryCode, t]);

  React.useEffect(() => {
    if (!isPrePurchase) {
      return;
    }

    const regionName = resolvedMarketRegion?.name || "";

    setPrePurchaseDetails((previous) => {
      if (previous.preferred_location === regionName) {
        return previous;
      }

      return {
        ...previous,
        preferred_location: regionName,
      };
    });
  }, [isPrePurchase, resolvedMarketRegion?.id, resolvedMarketRegion?.name]);

  React.useEffect(() => {
    let active = true;

    async function loadOssPartners() {
      /*
       * An OSS connection always belongs to
       * an explicit market region.
       */
      if (!resolvedMarketRegionId) {
        if (active) {
          setOssList([]);
          setSelectedOssId(null);
          setLoading(false);
        }

        return;
      }

      try {
        setLoading(true);
        setError("");

        const token = await getAccessTokenSilently();

        const params = new URLSearchParams();

        params.set("market_region_id", String(resolvedMarketRegionId));

        if (resolvedHomebuyerCountryCode) {
          params.set("country_code", resolvedHomebuyerCountryCode);
        }

        if (!isPrePurchase && propertyDetails?.postal_code) {
          params.set("postal_code", String(propertyDetails.postal_code).trim());
        }

        const response = await fetch(
          `${API_BASE}/api/homeowner/available/?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data?.detail || t("errors.loadFailed"));
        }

        if (!active) {
          return;
        }

        const items = Array.isArray(data) ? data : data.results || [];
        const availableItems = changeOssMode
          ? items.filter((oss) => {
              const organisationId = oss.organisation_id || oss.id;
              if (previousOssOrganisationId) {
                return String(organisationId) !== String(previousOssOrganisationId);
              }
              // Legacy dashboard responses may include only an OSS name.
              return String(oss.organisation_name || "").trim().toLowerCase() !==
                String(previousOssName || "").trim().toLowerCase();
            })
          : items;

        setOssList(availableItems);
        setSelectedOssId(
          availableItems?.[0]?.organisation_id || availableItems?.[0]?.id || null,
        );
      } catch (loadError) {
        if (!active) {
          return;
        }

        setOssList([]);
        setSelectedOssId(null);

        setError(loadError?.message || t("errors.loadFailed"));
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadOssPartners();

    return () => {
      active = false;
    };
  }, [
    getAccessTokenSilently,
    resolvedMarketRegionId,
    resolvedHomebuyerCountryCode,
    propertyDetails?.postal_code,
    isPrePurchase,
    changeOssMode,
    previousOssOrganisationId,
    previousOssName,
    t,
  ]);

  const selectedOss = React.useMemo(() => {
    return ossList.find(
      (item) => (item.organisation_id || item.id) === selectedOssId,
    );
  }, [ossList, selectedOssId]);

  const paidServices = React.useMemo(() => {
    return (selectedOss?.services || []).filter(
      (service) => service.service_type === "PAID",
    );
  }, [selectedOss]);

  const nonPaidServices = React.useMemo(() => {
    return (selectedOss?.services || []).filter(
      (service) => service.service_type === "NON_PAID",
    );
  }, [selectedOss]);

  const filteredOssList = React.useMemo(() => {
    if (filterType === "ADVISORY") {
      return ossList.filter((oss) => oss.is_advisory);
    }

    if (filterType === "TURNKEY") {
      return ossList.filter((oss) => oss.is_turnkey_renovation);
    }

    return ossList;
  }, [ossList, filterType]);

  function handleBack() {
    if (changeOssMode) {
      navigate("/hb/dashboard");
      return;
    }

    if (isPrePurchase) {
      navigate("/hb/dashboard");
      return;
    }

    navigate("/hb/oss/property-details", {
      state: {
        dossierId,
        propertyDetails,
        selectedMeasures,
        notes,
        epcFile,
        epcFileName,
        requestType: "RENOVATION",
        marketRegionId: resolvedMarketRegionId,
        marketRegion: resolvedMarketRegion,
        changeOssMode,
        previousOssRequestId,
        previousOssName,
        previousOssOrganisationId,
      },
    });
  }

  function validatePrePurchaseDetails() {
    if (!isPrePurchase) return true;

    if (!resolvedMarketRegionId) {
      setError(
        t("errors.marketRegionRequired", "Please select a market region."),
      );

      return false;
    }

    if (!prePurchaseDetails.preferred_location.trim()) {
      setError(t("errors.preferredLocationRequired"));
      return false;
    }

    if (!prePurchaseDetails.property_type) {
      setError(t("errors.propertyTypeRequired"));
      return false;
    }

    if (!prePurchaseDetails.timeline) {
      setError(t("errors.timelineRequired"));
      return false;
    }

    if (!prePurchaseDetails.budget_range.trim()) {
      setError(t("errors.budgetRangeRequired"));
      return false;
    }

    return true;
  }

  function handleNext() {
    if (!selectedOss) {
      setError(t("errors.selectOss"));
      return;
    }

    if (!validatePrePurchaseDetails()) {
      return;
    }

    setError("");
    setShowReviewModal(true);
  }

  React.useEffect(() => {
    if (showReviewModal) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    }
  }, [showReviewModal]);

  if (loading) {
    return (
      <div className="hb-oss-partners-page">
        <div className="hb-oss-partners-loading">
          <LoaderCircle className="hb-oss-spin" size={28} />
          <p>{t("loading")}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="hb-oss-partners-page">
      <div className="hb-oss-partners-shell">
        <div className="hb-oss-partners-hero">
          <div>
            <h1>{t("title")}</h1>
            <p>{t("subtitle")}</p>
          </div>

          <div className="hb-oss-partners-step">{t("stepLabel")}</div>
        </div>

        {changeOssMode ? (
          <div className="hb-oss-partners-alert" role="status" style={{
            background: "#eef5e6", color: "#31402f", borderColor: "#b8c9a7",
          }}>
            <strong>{t("changeOss.title", "Select a new One-Stop Shop")}</strong>
            <div>{t("changeOss.description", {
              defaultValue: "You are replacing {{ossName}}. You do not need to disconnect it first. Your renovation project and existing records will be preserved.",
              ossName: previousOssName || t("changeOss.currentOss", "your current One-Stop Shop"),
            })}</div>
          </div>
        ) : null}
        {error ? <div className="hb-oss-partners-alert">{error}</div> : null}

        <div
          className={`hb-oss-partners-layout ${
            isPrePurchase ? "hb-oss-partners-layout--pre-purchase" : ""
          }`}
        >
          <div className="hb-oss-partners-left-column">
            <section className="hb-oss-partners-card">
              <div className="hb-oss-partners-card__header">
                <div>
                  <h2>{t("sections.chooseOss")}</h2>
                  <p>{t("sections.chooseOssHint")}</p>
                </div>

                <span>{t("common.required")}</span>
              </div>

              <div className="hb-oss-partners-toolbar">
                {resolvedMarketRegion ? (
                  <div className="hb-oss-region-pill">
                    <MapPin size={15} />

                    <span>
                      {t("region.filteredBy", {
                        region:
                          resolvedMarketRegion.name ||
                          resolvedMarketRegion.code ||
                          "",
                      })}
                    </span>
                  </div>
                ) : null}

                <div className="hb-oss-filter-tabs">
                  {["ALL", "ADVISORY", "TURNKEY"].map((type) => (
                    <button
                      key={type}
                      type="button"
                      className={filterType === type ? "is-active" : ""}
                      onClick={() => setFilterType(type)}
                    >
                      {t(`filters.${type.toLowerCase()}`)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="hb-oss-partners-list" key={filterType}>
                {filteredOssList.length === 0 ? (
                  <div className="hb-oss-partners-empty">
                    {t("messages.noOssFound")}
                  </div>
                ) : (
                  filteredOssList.map((oss) => {
                    const ossId = oss.organisation_id || oss.id;
                    const isSelected = selectedOssId === ossId;

                    return (
                      <button
                        key={ossId}
                        type="button"
                        className={`hb-oss-partner-row ${
                          isSelected ? "is-selected" : ""
                        }`}
                        onClick={() => setSelectedOssId(ossId)}
                      >
                        <div>
                          <strong>{oss.organisation_name}</strong>
                          <p>
                            {formatRegionCountry(
                              oss?.market_region?.name ||
                                resolvedMarketRegion?.name ||
                                "",
                              oss.country_code,
                            )}
                          </p>

                          <div className="hb-oss-partner-tags">
                            {oss.is_advisory ? (
                              <span>{t("types.advisory")}</span>
                            ) : null}
                            {oss.is_turnkey_renovation ? (
                              <span>{t("types.turnkey")}</span>
                            ) : null}
                          </div>
                        </div>

                        {isSelected ? <CheckCircle2 size={20} /> : null}
                      </button>
                    );
                  })
                )}
              </div>
            </section>

            {isPrePurchase ? (
              <section className="hb-oss-partners-card hb-oss-search-card">
                <div className="hb-oss-partners-card__header">
                  <div>
                    <h2>{t("sections.searchCriteria")}</h2>
                    <p>{t("sections.searchCriteriaHint")}</p>
                  </div>

                  <span>{t("common.required")}</span>
                </div>

                <div className="hb-oss-search-grid">
                  <div className="hb-oss-search-field">
                    <label>
                      {t("fields.preferredLocation", "Preferred location")}

                      <span className="hb-oss-required">*</span>
                    </label>

                    <select
                      value={resolvedMarketRegionId || ""}
                      onChange={(event) => {
                        const value = event.target.value;

                        setSelectedMarketRegionId(value ? Number(value) : null);

                        setSelectedOssId(null);
                        setError("");
                      }}
                      disabled={
                        loadingMarketRegions || !resolvedHomebuyerCountryCode
                      }
                    >
                      <option value="">
                        {loadingMarketRegions
                          ? t(
                              "placeholders.loadingMarketRegions",
                              "Loading market regions...",
                            )
                          : t(
                              "placeholders.marketRegion",
                              "Select market region",
                            )}
                      </option>

                      {marketRegions.map((marketRegion) => (
                        <option key={marketRegion.id} value={marketRegion.id}>
                          {marketRegion.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="hb-oss-search-field">
                    <label>
                      {t("fields.propertyType")}
                      <span className="hb-oss-required">*</span>
                    </label>
                    <select
                      name="property_type"
                      value={prePurchaseDetails.property_type}
                      onChange={handlePrePurchaseChange}
                    >
                      <option value="">{t("placeholders.propertyType")}</option>
                      <option value="APARTMENT">
                        {t("options.propertyType.apartment")}
                      </option>
                      <option value="HOUSE">
                        {t("options.propertyType.house")}
                      </option>
                      <option value="TOWNHOUSE">
                        {t("options.propertyType.townhouse")}
                      </option>
                      <option value="OTHER">
                        {t("options.propertyType.other")}
                      </option>
                    </select>
                  </div>

                  <div className="hb-oss-search-field">
                    <label>
                      {t("fields.timeline")}
                      <span className="hb-oss-required">*</span>
                    </label>
                    <select
                      name="timeline"
                      value={prePurchaseDetails.timeline}
                      onChange={handlePrePurchaseChange}
                    >
                      <option value="">{t("placeholders.timeline")}</option>
                      <option value="IMMEDIATELY">
                        {t("options.timeline.immediately")}
                      </option>
                      <option value="THREE_MONTHS">
                        {t("options.timeline.threeMonths")}
                      </option>
                      <option value="SIX_MONTHS">
                        {t("options.timeline.sixMonths")}
                      </option>
                      <option value="NO_TIMELINE">
                        {t("options.timeline.noTimeline")}
                      </option>
                    </select>
                  </div>

                  <div className="hb-oss-search-field">
                    <label>
                      {t("fields.budgetRange")}
                      <span className="hb-oss-required">*</span>
                    </label>
                    <div className="hb-oss-budget-input">
                      <input
                        name="budget_range"
                        value={prePurchaseDetails.budget_range}
                        onChange={handlePrePurchaseChange}
                        placeholder={t("placeholders.budgetRange")}
                        inputMode="numeric"
                      />
                      <span>€</span>
                    </div>
                  </div>

                  <div className="hb-oss-search-field hb-oss-search-field--full">
                    <label>{t("fields.notes")}</label>
                    <textarea
                      name="notes"
                      rows={4}
                      value={prePurchaseDetails.notes}
                      onChange={handlePrePurchaseChange}
                      placeholder={t("placeholders.notes")}
                    />
                  </div>
                </div>

                <p className="hb-oss-search-help">
                  {t("messages.searchCriteriaHelp")}
                </p>
              </section>
            ) : null}
          </div>
          <aside className="hb-oss-selected-card">
            {selectedOss ? (
              <>
                <div className="hb-oss-selected-card__header">
                  <h2>{selectedOss.organisation_name}</h2>
                  <p>
                    {formatRegionCountry(
                      selectedOss?.market_region?.name ||
                        resolvedMarketRegion?.name ||
                        "",
                      selectedOss.country_code,
                    )}
                  </p>
                </div>

                {selectedOss.public_profile_summary ||
                selectedOss.short_description ? (
                  <p className="hb-oss-selected-description">
                    {selectedOss.public_profile_summary ||
                      selectedOss.short_description}
                  </p>
                ) : null}

                <ServiceSection
                  title={t("sections.nonPaidServices")}
                  icon={<Gift size={16} />}
                  emptyText={t("messages.noNonPaidServices")}
                  services={nonPaidServices}
                  language={language}
                  t={t}
                />

                <ServiceSection
                  title={t("sections.paidServices")}
                  icon={<Euro size={16} />}
                  emptyText={t("messages.noPaidServices")}
                  services={paidServices}
                  language={language}
                  t={t}
                  paid
                />
              </>
            ) : (
              <div className="hb-oss-partners-empty">
                {t("messages.selectPartner")}
              </div>
            )}
          </aside>
        </div>

        <div className="hb-oss-partners-footer">
          <button
            type="button"
            className="hb-oss-secondary-btn"
            onClick={handleBack}
          >
            <ArrowLeft size={16} />
            <span>{t("buttons.back")}</span>
          </button>

          <button
            type="button"
            className="hb-oss-primary-btn"
            onClick={handleNext}
            disabled={!selectedOss}
          >
            <span>{t("buttons.next")}</span>
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
      {showReviewModal ? (
        <HomebuyerOssReviewModal
          selectedOss={selectedOss}
          propertyDetails={propertyDetails}
          selectedMeasures={selectedMeasures}
          notes={isPrePurchase ? prePurchaseDetails.notes : notes}
          epcFileName={epcFileName}
          dossierId={dossierId}
          marketRegionId={resolvedMarketRegionId}
          marketRegion={resolvedMarketRegion}
          requestType={requestType}
          prePurchaseDetails={prePurchaseDetails}
          changeOssMode={changeOssMode}
          previousOssRequestId={previousOssRequestId}
          previousOssName={previousOssName}
          onClose={() => setShowReviewModal(false)}
          onSent={() => navigate("/hb/dashboard")}
        />
      ) : null}
    </div>
  );
}

function ServiceSection({
  title,
  icon,
  services,
  emptyText,
  language,
  t,
  paid = false,
}) {
  const [expandedServiceCodes, setExpandedServiceCodes] = React.useState({});

  function toggleService(serviceKey) {
    setExpandedServiceCodes((prev) => ({
      ...prev,
      [serviceKey]: !prev[serviceKey],
    }));
  }

  return (
    <div className="hb-oss-service-section">
      <div className="hb-oss-service-section__title">
        {icon}
        <span>{title}</span>
      </div>

      {services.length === 0 ? (
        <div className="hb-oss-service-empty">{emptyText}</div>
      ) : (
        <div className="hb-oss-service-list">
          {services.map((service) => {
            const serviceKey = service.id || service.service_code;
            const canExpand = paid && hasSubsidyExtraInfo(service);
            const isExpanded = Boolean(expandedServiceCodes[serviceKey]);

            return (
              <div
                key={serviceKey}
                className={`hb-oss-service-row ${
                  isExpanded ? "is-expanded" : ""
                }`}
              >
                <div className="hb-oss-service-row__main">
                  <div className="hb-oss-service-row__copy">
                    <strong>{getServiceLabel(service, language)}</strong>

                    {paid ? (
                      <p>
                        {service.subsidy_status === "YES"
                          ? t("services.subsidised")
                          : t("services.notSubsidised")}
                      </p>
                    ) : (
                      <p>{t("services.freeOfCharge")}</p>
                    )}
                  </div>

                  <div className="hb-oss-service-row__side">
                    {paid ? (
                      <span>
                        {service.approximate_cost_text
                          ? formatCost(service.approximate_cost_text)
                          : t("services.costOnRequest")}
                      </span>
                    ) : (
                      <span>{t("services.free")}</span>
                    )}

                    {canExpand ? (
                      <button
                        type="button"
                        className="hb-oss-service-expand-btn"
                        onClick={() => toggleService(serviceKey)}
                        aria-expanded={isExpanded}
                        aria-label={
                          isExpanded
                            ? t("services.hideSubsidyInfo")
                            : t("services.showSubsidyInfo")
                        }
                      >
                        <ChevronDown size={16} />
                      </button>
                    ) : null}
                  </div>
                </div>

                {canExpand && isExpanded ? (
                  <div className="hb-oss-service-subsidy-details">
                    <div className="hb-oss-service-subsidy-details__title">
                      {t("services.subsidyInformation")}
                    </div>

                    {service.subsidy_information_label ? (
                      <p>
                        <strong>{t("services.linkLabel")}:</strong>{" "}
                        {service.subsidy_information_label}
                      </p>
                    ) : null}

                    {service.subsidy_information_url ? (
                      <a
                        href={service.subsidy_information_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <ExternalLink size={14} />
                        <span>
                          {service.subsidy_information_label ||
                            t("services.openSubsidyLink")}
                        </span>
                      </a>
                    ) : null}

                    {service.subsidy_notes ? (
                      <p>
                        <strong>{t("services.notes")}:</strong>{" "}
                        {service.subsidy_notes}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
